import { Type } from "@sinclair/typebox";
import { TranslatedString } from "../../../config/common";
import { HelpContentType } from "../../../config/card-components";
export const BaseContent = Type.Object({}, {
    title: "BaseContent",
});
export const ContentText = Type.Composite([
    BaseContent,
    Type.Object({
        type: Type.Literal("text"),
        text: Type.String(),
        /** Frontend-only concept used for rendering typing indicator on agent replies */
        isDraft: Type.Optional(Type.Boolean()),
    }, {
        title: "ContentText",
    }),
]);
export const ContentHtml = Type.Composite([
    BaseContent,
    Type.Object({
        type: Type.Literal("html"),
        html: Type.String(),
    }, {
        title: "ContentHtml",
    }),
]);
export const ContentMarkdown = Type.Composite([
    BaseContent,
    Type.Object({
        type: Type.Literal("markdown"),
        markdown: Type.String(),
    }, {
        title: "ContentMarkdown",
    }),
]);
export const BaseMediaContentSchema = Type.Composite([
    BaseContent,
    Type.Object({
        media_id: Type.String({ format: "uuid" }),
        media_url: Type.Optional(Type.String()),
        name: Type.Union([Type.String(), Type.Null()]),
        size: Type.Union([Type.Number(), Type.Null()]),
        content_type: Type.Union([Type.String(), Type.Null()]),
    }, {
        title: "BaseMediaContent",
    }),
]);
export const ContentFile = Type.Composite([
    BaseMediaContentSchema,
    Type.Object({
        type: Type.Literal("file"),
    }, {
        title: "ContentFile",
    }),
]);
export const ChatbotContentFile = Type.Object({
    type: Type.Literal("outputFile"),
    file: Type.Optional(Type.Object({
        blob_id: Type.String(),
        content_type: Type.String(),
    })),
}, {
    title: "ChatbotContentFile",
});
export const ContentImage = Type.Composite([
    BaseMediaContentSchema,
    Type.Object({
        type: Type.Literal("image"),
        expires_at: Type.Optional(Type.String()),
    }, {
        title: "ContentImage",
    }),
]);
export const ContentAudio = Type.Composite([
    BaseMediaContentSchema,
    Type.Object({
        type: Type.Literal("audio"),
        expires_at: Type.Optional(Type.String()),
    }, {
        title: "ContentAudio",
    }),
]);
export const ContentVideo = Type.Composite([
    BaseMediaContentSchema,
    Type.Object({
        type: Type.Literal("video"),
        expires_at: Type.Optional(Type.String()),
    }, {
        title: "ContentVideo",
    }),
]);
export const StaticContent = Type.Union([ContentText, ContentHtml, ContentMarkdown]);
export const MediaContent = Type.Union([ContentFile, ContentImage, ContentVideo, ContentAudio]);
export const ChatbotValueRecord = Type.Record(Type.String({ title: "Chatbot input ID" }), Type.String({ title: "input value" }));
export const BaseChatbotContent = Type.Object({
    id: Type.String(),
}, {
    title: "BaseChatbotContent",
});
export const ChatbotAction = Type.Object({
    id: Type.String(),
}, {
    title: "ChatbotAction",
});
export const ChatbotContentChoiceOption = Type.Object({
    id: Type.String(),
    label: Type.Optional(TranslatedString),
    is_selected: Type.Boolean(),
    next: ChatbotAction,
}, {
    title: "ChatbotContentChoiceOption",
});
export const ChatbotContentChoice = Type.Composite([
    BaseChatbotContent,
    Type.Object({
        type: Type.Literal("inputChoice"),
        choiceType: Type.Union([Type.Literal("dropdown"), Type.Literal("button")]),
        placeholder: Type.Optional(TranslatedString),
        choices: Type.Array(ChatbotContentChoiceOption),
        value: Type.Optional(ChatbotValueRecord),
        label: Type.Optional(ChatbotValueRecord),
    }, {
        title: "ChatbotContentChoice",
    }),
]);
export const ChatbotBooleanInput = Type.Union([
    Type.Literal("true_value"),
    Type.Literal("false_value"),
]);
const ChatbotBooleanInputValueRecord = Type.Record(Type.String({ title: "Chatbot boolean input ID" }), ChatbotBooleanInput);
export const ChatbotContentInputBoolean = Type.Composite([
    BaseChatbotContent,
    Type.Object({
        type: Type.Literal("inputBoolean"),
        trueLabel: TranslatedString,
        falseLabel: TranslatedString,
        value: Type.Optional(ChatbotBooleanInputValueRecord),
    }, {
        title: "ChatbotContentInputBoolean",
    }),
]);
export const ChatbotContentInputName = Type.Composite([
    BaseChatbotContent,
    Type.Object({
        type: Type.Literal("inputName"),
        placeholder: Type.Optional(TranslatedString),
        value: Type.Optional(ChatbotValueRecord),
    }, {
        title: "ChatbotContentInputName",
    }),
]);
export const ChatbotContentInputFile = Type.Composite([
    BaseChatbotContent,
    Type.Object({
        type: Type.Literal("inputFile"),
        buttonText: Type.Optional(TranslatedString),
        value: Type.Optional(Type.Record(Type.String({ title: "Chatbot input ID" }), MediaContent)),
    }, {
        title: "ChatbotContentInputFile",
    }),
]);
export const ChatbotContentInputEmail = Type.Composite([
    BaseChatbotContent,
    Type.Object({
        type: Type.Literal("inputEmail"),
        placeholder: Type.Optional(TranslatedString),
        value: Type.Optional(ChatbotValueRecord),
    }, {
        title: "ChatbotContentInputEmail",
    }),
]);
export const ChatbotContentInputNumber = Type.Composite([
    BaseChatbotContent,
    Type.Object({
        type: Type.Literal("inputNumber"),
        placeholder: Type.Optional(TranslatedString),
        value: Type.Optional(ChatbotValueRecord),
    }, {
        title: "ChatbotContentInputNumber",
    }),
]);
export const ChatbotContentInputLink = Type.Composite([
    BaseChatbotContent,
    Type.Object({
        type: Type.Literal("inputLink"),
        placeholder: Type.Optional(TranslatedString),
        value: Type.Optional(ChatbotValueRecord),
    }, {
        title: "ChatbotContentInputLink",
    }),
]);
export const ChatbotContentInputPhone = Type.Composite([
    BaseChatbotContent,
    Type.Object({
        type: Type.Literal("inputPhoneNumber"),
        placeholder: Type.Optional(TranslatedString),
        value: Type.Optional(ChatbotValueRecord),
    }, {
        title: "ChatbotContentInputPhone",
    }),
]);
export const ChatbotContentInputText = Type.Composite([
    BaseChatbotContent,
    Type.Object({
        type: Type.Literal("inputText"),
        placeholder: Type.Optional(TranslatedString),
        value: Type.Optional(ChatbotValueRecord),
        multiline: Type.Optional(Type.Boolean()),
    }, {
        title: "ChatbotContentInputText",
    }),
]);
export const ChatbotContentInputDatetime = Type.Composite([
    BaseChatbotContent,
    Type.Object({
        type: Type.Literal("inputDatetime"),
        placeholder: Type.Optional(TranslatedString),
        value: Type.Optional(ChatbotValueRecord),
        date_format: Type.Union([Type.Literal("date"), Type.Literal("dateTime")]), // TODO Optional
    }, {
        title: "ChatbotContentInputDatetime",
    }),
]);
export const ChatbotInputAddressValue = Type.Object({
    // line 1-3 can accomodate: Business name, apartment, flat number, suite, street address, development/estate.
    line1: Type.String(),
    line2: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    line3: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    city: Type.String(), // City, Town
    countyOrProvince: Type.Optional(Type.Union([Type.String(), Type.Null()])), // State, Province, County, Prefecture
    postalCode: Type.Optional(Type.Union([Type.String(), Type.Null()])), // Post code, Zip Code
    country: Type.String(), // Country name
    other: Type.Optional(Type.Union([Type.String(), Type.Null()])), // Lat/long, what three words, instructions.
});
export const ChatbotContentInputAddress = Type.Composite([
    BaseChatbotContent,
    Type.Object({
        type: Type.Literal("inputAddress"),
        title: TranslatedString,
        value: Type.Optional(Type.Record(Type.String({ title: "Chatbot input ID" }), ChatbotInputAddressValue)),
    }, {
        title: "ChatbotContentInputAddress",
    }),
]);
export const ChatbotContentInputDropdown = Type.Composite([
    BaseChatbotContent,
    Type.Object({
        type: Type.Literal("inputDropdown"),
        placeholder: Type.Optional(TranslatedString),
        value: Type.Optional(ChatbotValueRecord),
        options: Type.Array(Type.Object({
            id: Type.String(),
            value: Type.String(),
        }, {
            title: "ChatbotContentDropdownOption",
        })),
    }, {
        title: "ChatbotContentInputDropdown",
    }),
]);
export const ChatbotCustomCard = Type.Object({
    type: Type.Literal("card"),
    showUrl: Type.Optional(Type.Boolean()),
    cardTitle: Type.Optional(TranslatedString),
    cardDescription: Type.Optional(TranslatedString),
    cardUrl: Type.String(),
    file: Type.Optional(Type.Object({
        blob_id: Type.String(),
        content_type: Type.String(),
        workflow_revision_id: Type.Optional(Type.Number()),
    }, {
        title: "ChatbotCustomCardFile",
    })),
}, {
    title: "ChatbotCustomCard",
});
export const ChatbotContentCallbackOption = Type.Object({
    id: Type.String(),
    type: Type.Literal("callbackOption"),
    label: TranslatedString,
}, {
    title: "ChatbotCallbackOption",
});
export const ChatbotContentHelpfulSurvey = Type.Object({
    type: Type.Literal("helpfulSurvey"),
    question: StaticContent,
    yes: ChatbotContentCallbackOption,
    no: ChatbotContentCallbackOption,
}, {
    title: "ChatbotHelpfulSurvey",
});
export const ChatbotContentAiResponse = Type.Object({
    type: Type.Literal("aiResponse"),
    answer: StaticContent,
    survey: Type.Optional(ChatbotContentHelpfulSurvey),
}, {
    title: "ChatbotAiResponse",
});
const EmbeddedContentCustom = Type.Object({
    type: Type.Literal("embeddedContent"),
    contentType: Type.Literal("custom"),
    code: Type.String(),
});
const EmbeddedContentYoutube = Type.Object({
    type: Type.Literal("embeddedContent"),
    contentType: Type.Literal("youtube"),
    videoId: Type.String(),
    title: Type.Optional(TranslatedString),
    description: Type.Optional(TranslatedString),
});
const EmbeddedContentVimeo = Type.Object({
    type: Type.Literal("embeddedContent"),
    contentType: Type.Literal("vimeo"),
    videoId: Type.String(),
    title: Type.Optional(TranslatedString),
    description: Type.Optional(TranslatedString),
});
export const ChatbotEmbeddedContent = Type.Union([
    EmbeddedContentCustom,
    EmbeddedContentYoutube,
    EmbeddedContentVimeo,
]);
export const ChatbotHelpCenterContent = Type.Object({
    type: Type.Literal("helpCenterContent"),
    link: Type.Union([Type.Literal("messenger"), Type.Literal("web")]),
    display: Type.Union([
        Type.Literal("link"),
        Type.Literal("article_short"),
        Type.Literal("article_long"),
    ]),
    content: Type.Array(Type.Object({
        id: Type.String(),
        type: HelpContentType,
        title: Type.Optional(Type.String()),
    }, {
        title: "ChatbotHelpCenterContentItem",
    })),
}, {
    title: "ChatbotHelpCenterContent",
});
export const ReplyBoxInputContent = Type.Object({
    // id: Type.String(),
    type: Type.Literal("replyBoxInput"),
    allowAttachments: Type.Optional(Type.Boolean()),
    value: Type.Optional(Type.String()),
}, {
    title: "ChatbotReplyBoxInputContent",
});
export const ChatbotContent = Type.Union([
    ChatbotContentChoice,
    ChatbotContentInputBoolean,
    ChatbotContentInputName,
    ChatbotContentInputEmail,
    ChatbotContentInputPhone,
    ChatbotContentInputNumber,
    ChatbotContentInputText,
    ChatbotContentInputFile,
    ChatbotContentInputAddress,
    ChatbotHelpCenterContent,
    ChatbotCustomCard,
    ChatbotContentInputLink,
    ChatbotContentInputDatetime,
    ChatbotContentInputDropdown,
    ChatbotContentFile,
    ChatbotContentAiResponse,
    ChatbotContentCallbackOption,
    ChatbotEmbeddedContent,
]);
export const MessageContent = Type.Union([
    StaticContent,
    MediaContent,
    ChatbotContent,
    ReplyBoxInputContent,
]);
export const CreateChatbotContent = Type.Union([
    Type.Object({
        type: Type.Literal("inputChoice"),
        value: Type.Record(Type.String({ title: "Chatbot choice ID" }), Type.String({ title: "choice.next.id" })),
        label: Type.Record(Type.String({ title: "Chatbot choice ID" }), Type.String({ title: "choice.label" })),
    }),
    Type.Object({
        type: Type.Literal("inputName"),
        value: ChatbotValueRecord,
    }),
    Type.Object({
        type: Type.Literal("inputBoolean"),
        value: ChatbotValueRecord,
    }),
    Type.Object({
        type: Type.Literal("inputEmail"),
        value: ChatbotValueRecord,
    }),
    Type.Object({
        type: Type.Literal("inputPhoneNumber"),
        value: ChatbotValueRecord,
    }),
    Type.Object({
        type: Type.Literal("inputText"),
        value: ChatbotValueRecord,
    }),
    Type.Object({
        type: Type.Literal("inputNumber"),
        value: ChatbotValueRecord,
    }),
    Type.Object({
        type: Type.Literal("inputDatetime"),
        value: ChatbotValueRecord,
    }),
    Type.Object({
        type: Type.Literal("inputDropdown"),
        value: ChatbotValueRecord,
    }),
    Type.Object({
        type: Type.Literal("inputFile"),
        value: Type.Record(Type.String({ title: "Chatbot input ID" }), MediaContent),
    }),
    Type.Object({
        type: Type.Literal("inputLink"),
        value: ChatbotValueRecord,
    }),
    Type.Object({
        type: Type.Literal("inputAddress"),
        value: Type.Record(Type.String({ title: "Chatbot input ID" }), ChatbotInputAddressValue),
    }),
    Type.Object({
        type: Type.Literal("callbackOption"),
        label: TranslatedString,
        id: Type.String(),
    }),
]);
export const CreateMessageContent = Type.Union([
    StaticContent,
    MediaContent,
    CreateChatbotContent,
    ReplyBoxInputContent,
]);
